/**
 * Centralized Google Maps loader to prevent multiple conflicting loads
 */

import { clientKeys } from '@/config/clientKeys';

interface GoogleMapsLoaderOptions {
  libraries?: string[];
  callback?: () => void;
}

class GoogleMapsLoader {
  private static instance: GoogleMapsLoader;
  private loadPromise: Promise<void> | null = null;
  private isLoaded = false;
  private loadedLibraries: Set<string> = new Set();

  private constructor() {}

  static getInstance(): GoogleMapsLoader {
    if (!GoogleMapsLoader.instance) {
      GoogleMapsLoader.instance = new GoogleMapsLoader();
    }
    return GoogleMapsLoader.instance;
  }

  async load(options: GoogleMapsLoaderOptions = {}): Promise<void> {
    const { libraries = ['places', 'marker'], callback } = options;

    // If already loading, wait for it
    if (this.loadPromise) {
      await this.loadPromise;
      if (callback) callback();
      return;
    }

    // If already loaded, check if we need additional libraries
    if (this.isLoaded && window.google?.maps) {
      const needsReload = libraries.some(lib => !this.loadedLibraries.has(lib));
      if (!needsReload) {
        if (callback) callback();
        return;
      }
      // If we need additional libraries, we need to reload
      // This is not ideal but Google Maps doesn't support adding libraries dynamically
      console.warn('Google Maps needs to reload with additional libraries:', libraries);
    }

    // Create the load promise
    this.loadPromise = new Promise<void>((resolve, reject) => {
      // Check if script already exists
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        // Wait for it to load
        const checkLoaded = () => {
          if (window.google?.maps?.places && window.google?.maps?.marker) {
            this.isLoaded = true;
            libraries.forEach(lib => this.loadedLibraries.add(lib));
            resolve();
            if (callback) callback();
          } else {
            setTimeout(checkLoaded, 100);
          }
        };
        checkLoaded();
        return;
      }

      // Create new script
      const script = document.createElement('script');
      const allLibraries = Array.from(new Set([...libraries, 'places', 'marker', 'visualization']));
      script.src = `https://maps.googleapis.com/maps/api/js?key=${clientKeys.GOOGLE_MAPS_API_KEY}&libraries=${allLibraries.join(',')}`;
      script.async = true;
      script.defer = true;

      // Set up global callback
      (window as any).initGoogleMaps = () => {
        this.isLoaded = true;
        allLibraries.forEach(lib => this.loadedLibraries.add(lib));
        resolve();
        if (callback) callback();
        delete (window as any).initGoogleMaps;
      };

      script.src += '&callback=initGoogleMaps';

      script.onerror = () => {
        this.loadPromise = null;
        reject(new Error('Failed to load Google Maps'));
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  isGoogleMapsLoaded(): boolean {
    return this.isLoaded && !!window.google?.maps;
  }

  waitForMaps(timeout = 5000): Promise<void> {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      
      const check = () => {
        if (window.google?.maps?.places) {
          resolve();
        } else if (Date.now() - startTime > timeout) {
          reject(new Error('Timeout waiting for Google Maps to load'));
        } else {
          setTimeout(check, 100);
        }
      };
      
      check();
    });
  }
}

export const googleMapsLoader = GoogleMapsLoader.getInstance();
